import React, { useContext, useEffect, useRef, useState } from 'react';
import Share from 'components/Dashboard/components/Share';
import { store } from 'components/Store';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';

import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import RateUpdatesContentButton from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContentButton';
import ContentPagePriorityBadge from 'components/Dashboard/ItemController/ContentPagePriorityBadge';
import LegislationOverviewContent from 'components/Dashboard/Legislation/LegislationOverviewContent';
import LegislationOverviewTab from 'components/Dashboard/Legislation/LegislationOverviewTab';
import LegislationRecentUpdatesTab from 'components/Dashboard/Legislation/LegislationRecentUpdatesTab';
import useLegislationRecentUpdates from 'components/Dashboard/Legislation/useLegislationRecentUpdates';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  monthsShort: ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
});
const ShareButtonRender = (props) => {
  const { shareButtonWidget, currentNew, embed } = props;
  const { id, billTitle, title, canonicalUrl, objectId, objectType } = currentNew;
  let item = {
    id,
    title: billTitle ? billTitle : title,
    objectID: objectId,
    type: objectType,
  };
  const ShareOptions = {
    item,
    id: item.id,
    title: item.title,
    shareButtonWidget,
    itemShare: embed,
  };
  if (canonicalUrl) {
    ShareOptions.link = `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}`;
  }
  return <Share {...ShareOptions} />;
};

const LegislationNewContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;

  const { activeSearch } = search;
  const {
    currentNew,
    shareButtonWidget,
    contentContainer,
    hideSummary,
    reportWidget,
    embed,
    scrollbarRef,
    scrolling,
    isFromPopup,
    notIncludeBranding,
    notShowShare,
  } = props;

  const { createMarkup } = useCreateMarkup();

  const titleToRender = () => {
    let title;
    if (activeSearch || shareButtonWidget) {
      title = currentNew.billTitle
        ? (currentNew.billTitleHighlighted ?? currentNew.billTitle)
        : (currentNew.titleHighlighted ?? currentNew.title);
    } else {
      title = currentNew.billTitle ?? currentNew.title;
    }
    return title;
  };

  const mainContentContainerRef = useRef(null);

  const titleSection = useRef(null);
  let { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection });

  const [activeTab, setActiveTab] = useState('overview');
  const [heightContainer, containerRef] = useHeightContainer();
  const {
    updates,
    updatesTotal,
    isLoading: isUpdatesLoading,
    loadLegislationRecentUpdates,
  } = useLegislationRecentUpdates({
    legislationId: currentNew?.id,
  });

  useEffect(() => {
    const container = mainContentContainerRef.current;

    if (container && getAppSite() === 'usa') {
      const styleTags = container.querySelectorAll('style');
      styleTags.forEach((tag) => tag.parentNode.removeChild(tag));
    }
  }, [reportWidget, currentNew?.id]);

  useEffect(() => {
    if (!isUpdatesLoading && updatesTotal === 0 && activeTab === 'updates') {
      setActiveTab('overview');
    }
  }, [isUpdatesLoading, updatesTotal, activeTab]);

  return (
    <div ref={mainContentContainerRef}>
      <div
        className={`${
          reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
        } pb-2 legislation-content-title`}
        ref={titleSection}
      >
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(titleToRender())} />
        <div className='row mx-0 justify-content-between align-items-center'>
          {getAppSite() === 'usa' ? (
            <p className='heading-date flex-grow-1 px-0 mb-0'>
              {currentNew.firstEventDate && (
                <>
                  <span className='date'>
                    Introduced:{' '}
                    {time(currentNew.firstEventDate, { utc: true, skipTimezone: true }).formatSimpleGeneralDate()}
                  </span>
                </>
              )}
              {currentNew.docUrl && (
                <>
                  <span className='dot'></span>
                  <a
                    className='legislation-source legislation-source-size mb-0'
                    href={currentNew.docUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {reportWidget ? 'Source' : 'Go to source'}
                  </a>
                </>
              )}
              <ContentPagePriorityBadge
                currentNew={currentNew}
                showDot={!!currentNew.firstEventDate || !!currentNew.docUrl}
              />
            </p>
          ) : (
            <p className='heading-date mb-0'>
              {!reportWidget && (
                <a
                  className='legislation-source legislation-source-size mb-0'
                  href={currentNew.url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {'Go to source'}
                </a>
              )}
              <ContentPagePriorityBadge currentNew={currentNew} showDot={!reportWidget && !!currentNew.url} />
            </p>
          )}
          {!notShowShare && (
            <div className='flex-centered'>
              {!notIncludeBranding && (
                <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
              )}
            </div>
          )}
        </div>
        <div className='my-2'>
          {!reportWidget && <RateUpdatesContentButton currentNew={currentNew} />}
        </div>
      </div>
      {!reportWidget && (
        <div
          className={`px-3 px-lg-4 ${!isUpdatesLoading && updatesTotal > 0 ? 'pb-3 border-bottom' : ''}`}
          ref={containerRef}
        >
          {!isUpdatesLoading && updatesTotal > 0 && (
            <>
              <button
                className={`general-button ${activeTab === 'overview' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1 mr-3`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`general-button ${activeTab === 'updates' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1`}
                onClick={() => setActiveTab('updates')}
              >
                Recent updates ({updatesTotal})
              </button>
            </>
          )}
        </div>
      )}
      {reportWidget && !shareButtonWidget && <ContentSummary content={currentNew} hide={hideSummary} />}
      <div className={`pb-5 specific-content-container ${reportWidget ? 'px-3 px-lg-4' : ''}`}>
        {/* FIXED TITLE */}
        <div
          className={`content-item-scrolled-title main-sticky section-title bg-white px-3 px-lg-4 ${
            showFixedTitle && !isFromPopup && !reportWidget ? 'item-scrolled-title-visible' : ''
          }`}
          style={titleStyles}
        >
          <div className='d-flex justify-content-between scrolled-title-container'>
            <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(titleToRender())} />
            {!notShowShare && (
              <>
                {!notIncludeBranding && (
                  <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} />
                )}
              </>
            )}
          </div>
        </div>
        {/* END FIXED TITLE */}
        {reportWidget ? (
          <LegislationOverviewContent
            currentNew={currentNew}
            scrollbarRef={scrollbarRef}
            scrolling={scrolling}
            reportWidget={reportWidget}
          />
        ) : activeTab === 'overview' ? (
          <LegislationOverviewTab
            currentNew={currentNew}
            heightContainer={heightContainer}
            hideSummary={hideSummary}
            shareButtonWidget={shareButtonWidget}
          />
        ) : (
          <LegislationRecentUpdatesTab
            updates={updates}
            updatesTotal={updatesTotal}
            isLoading={isUpdatesLoading}
            loadLegislationRecentUpdates={loadLegislationRecentUpdates}
            heightContainer={heightContainer}
          />
        )}
      </div>
    </div>
  );
};

export default LegislationNewContent;
